// vaultCrypto.js
//
// Zero-knowledge encryption for the Password Manager, with a recovery-key
// fallback so a forgotten master password isn't a permanent lockout.
//
// Design:
//   - A random 256-bit "vault key" is generated once, at setup. This is the
//     key that actually encrypts every entry. It is never stored directly.
//   - The vault key is encrypted TWICE and both copies are stored in
//     Firestore's vaultMeta doc:
//       1) encrypted with a key derived from the MASTER PASSWORD
//       2) encrypted with a key derived from a randomly generated
//          RECOVERY KEY, shown to the user once at setup time
//   - To unlock, either the master password OR the recovery key can be
//     used to decrypt its respective copy and recover the vault key.
//   - Losing both the master password AND the recovery key means the
//     vault is unrecoverable — that's inherent to zero-knowledge design.
//
// Uses only the browser's native SubtleCrypto — no external dependency.

const PBKDF2_ITERATIONS = 250000;
const SALT_LENGTH_BYTES = 16;
const IV_LENGTH_BYTES = 12;
const VAULT_KEY_LENGTH_BYTES = 32; // 256-bit AES key

// --- random material ---

function randomBase64(byteLength) {
  const bytes = crypto.getRandomValues(new Uint8Array(byteLength));
  return bufferToBase64(bytes);
}

export function generateSalt() {
  return randomBase64(SALT_LENGTH_BYTES);
}

/** The actual key that encrypts vault entries. Generated once, at setup. */
export function generateVaultKeyMaterial() {
  return randomBase64(VAULT_KEY_LENGTH_BYTES);
}

/**
 * A human-writable recovery code, e.g. "K7XR-9PQD-3MFH-2WZT-8LNB-4CVY".
 * Excludes visually ambiguous characters (0/O, 1/I/L).
 */
export function generateRecoveryCode() {
  const charset = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";
  const groups = [];
  for (let g = 0; g < 6; g++) {
    let group = "";
    const randomValues = crypto.getRandomValues(new Uint8Array(4));
    for (let i = 0; i < 4; i++) {
      group += charset[randomValues[i] % charset.length];
    }
    groups.push(group);
  }
  return groups.join("-");
}

// --- key derivation (password or recovery code -> AES-GCM key) ---

export async function deriveKey(secret, saltBase64) {
  const encoder = new TextEncoder();
  const salt = base64ToBuffer(saltBase64);

  const baseKey = await crypto.subtle.importKey(
    "raw",
    encoder.encode(secret),
    "PBKDF2",
    false,
    ["deriveKey"]
  );

  return crypto.subtle.deriveKey(
    { name: "PBKDF2", salt, iterations: PBKDF2_ITERATIONS, hash: "SHA-256" },
    baseKey,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
}

/** Import raw vault-key bytes (base64) as a usable AES-GCM CryptoKey. */
export async function importVaultKey(vaultKeyMaterialBase64) {
  const raw = base64ToBuffer(vaultKeyMaterialBase64);
  return crypto.subtle.importKey("raw", raw, "AES-GCM", false, ["encrypt", "decrypt"]);
}

// --- encrypt / decrypt ---

export async function encryptField(key, plaintext) {
  const encoder = new TextEncoder();
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH_BYTES));
  const ciphertextBuffer = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, encoder.encode(plaintext));
  return { ciphertext: bufferToBase64(new Uint8Array(ciphertextBuffer)), iv: bufferToBase64(iv) };
}

export async function decryptField(key, ciphertextBase64, ivBase64) {
  const iv = base64ToBuffer(ivBase64);
  const ciphertext = base64ToBuffer(ciphertextBase64);
  const plaintextBuffer = await crypto.subtle.decrypt({ name: "AES-GCM", iv }, key, ciphertext);
  return new TextDecoder().decode(plaintextBuffer);
}

// --- canary (lets the UI say "wrong password" instead of a generic crypto error) ---

export async function createCanary(key) {
  return encryptField(key, "vault-unlock-check");
}

export async function verifyCanary(key, canaryCiphertext, canaryIv) {
  try {
    const result = await decryptField(key, canaryCiphertext, canaryIv);
    return result === "vault-unlock-check";
  } catch {
    return false;
  }
}

// --- base64 helpers ---

function bufferToBase64(buffer) {
  let binary = "";
  for (let i = 0; i < buffer.byteLength; i++) binary += String.fromCharCode(buffer[i]);
  return btoa(binary);
}

function base64ToBuffer(base64) {
  const binary = atob(base64);
  const buffer = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) buffer[i] = binary.charCodeAt(i);
  return buffer;
}
